import { Container } from 'pixi.js';
import type { ScoreBlockerModel } from '../../models/blockers/ScoreBlockerModel';
import type { BlockerView } from '../../types';
import { createBlockerLockedSprite } from './utils/createBlockerLockedSprite';
import { soundManager } from '../../../../app/SoundManager';
import { FXParticleLayer } from '../../../../libs/particles/FXParticleLayer';
import { getRandomElement } from '../../../../utils/array-utils';
import { ScoreBlockerFieldLayout } from '../../../../../layouts/blockers/ScoreBlockerFieldLayout';
import type { FXParticleEmitterConfig } from '../../../../libs/particles/FXParticleEmitterConfig';
import emitterJSON from '../../../../../assets/particles{ignore}/Score_Blocker_Unlock_Particles.json';

const aboutToCompleteScoreRange = 20;

export class ScoreBlockerFieldView extends Container implements BlockerView {
  private readonly particleLayer = new FXParticleLayer(true).props({
    parent: this,
  });

  private readonly layout = new ScoreBlockerFieldLayout();

  private readonly unlockSprite = createBlockerLockedSprite().props({
    visible: false,
  });

  constructor(private readonly model: ScoreBlockerModel) {
    super();

    this.addChild(this.layout.root, this.unlockSprite);

    const onProgressChanged = (current: number, target: number): void => {
      model.onProgressChanged.disconnect(onProgressChanged);
      if (target - current <= aboutToCompleteScoreRange) {
        this.startAttractLoop();
      }
    };
    model.onProgressChanged.connect(onProgressChanged);

    this.setScore(model.getScore());
  }

  public getModel(): ScoreBlockerModel {
    return this.model;
  }

  private startAttractLoop(): void {
    if (this.destroyed) return;

    const loopDelay = 2000;
    const initialAngleOffset = 20;
    const bounces = 4;
    let bouncesRemaining = bounces;
    const elasticity = 0.6;
    const tweenDuration = 250;
    const { lockContainer, scoreText } = this.layout;

    let toAngle = initialAngleOffset;

    const createLoopTweens = (): void => {
      const delay = bouncesRemaining === bounces ? loopDelay : 0;

      if (bouncesRemaining === bounces) {
        scoreText.removeTweens();
        scoreText.tweenTo({ scaleX: 1.1, scaleY: 1.1 }, (tweenDuration * bounces) / 2, 'sinIn', true, delay, 0, true);
      }

      lockContainer.removeTweens();
      lockContainer.tweenTo({ angle: toAngle }, tweenDuration, 'sinOut', true, delay).onComplete.addOnce(() => {
        lockContainer.tweenTo({ angle: 0 }, tweenDuration, 'sinOut', true).onComplete.addOnce(() => {
          const bouncesComplete = bouncesRemaining === 0;
          toAngle = !bouncesComplete ? toAngle * -elasticity : initialAngleOffset;
          bouncesRemaining += bouncesComplete ? bounces : -1;
          // FIXME: investigate this was activating sometimes even though the Tweens were stopped.
          if (!this.model.isUnlocked()) createLoopTweens();
        });
      });
    };
    createLoopTweens();
  }

  public setScore(score: number): void {
    this.layout.scoreText.text = score;
  }

  public async animateRemoval(): Promise<void> {
    if (this.destroyed) return;
    soundManager.play(`Gameplay_Unlock_Tile`);

    const { lockContainer, lockAndChainContainer, scoreText, tileDarkness, tileHighlight } = this.layout;
    const { unlockSprite, particleLayer } = this;

    scoreText.removeTweens();
    lockContainer.removeTweens();

    if (lockContainer.angle !== 0) await lockContainer.tweenTo({ angle: 0 }, 100, 'quadOut', true).asyncOnComplete();

    // Show animated sprite in place of container in layout.
    lockAndChainContainer.visible = false;
    unlockSprite.visible = true;

    // Animate darkness out / highlight flash.
    tileDarkness.tweenTo({ alpha: 0 }, 800, 'quadOut', true, 150);
    tileHighlight.tweenTo({ alpha: 1 }, 400, 'quadOut', true, 150, 0, true);

    // Animate out score text
    scoreText.tweenTo({ scaleX: 0, scaleY: 0, alpha: 0 }, 250, 'quadOut', true, 0);

    const emitter = particleLayer.addParticleEmitter(0, 5, {
      ...emitterJSON,
      startDelay: 100,
    } as FXParticleEmitterConfig);
    emitter.randomDataGenerator.sow([getRandomElement([2, 5, 8])]);

    await this.animateOutLock();
  }

  private async animateOutLock(): Promise<void> {
    const { unlockSprite } = this;
    const unlockSpriteOutDelay = 300;
    const scaleOutDuration = 300;
    const fadeOutDuration = 50;

    await new Promise((resolve) => {
      unlockSprite.onComplete = (): void => {
        unlockSprite.tweenTo({ scaleX: 0, scaleY: 0 }, scaleOutDuration, 'backIn', true, unlockSpriteOutDelay);
        unlockSprite
          .tweenTo(
            { alpha: 0 },
            fadeOutDuration,
            'quadOut',
            true,
            unlockSpriteOutDelay + scaleOutDuration - fadeOutDuration,
          )
          .onComplete.addOnce(resolve);
      };
      unlockSprite.play();
    });
  }

  public destroy(): void {
    this.particleLayer.destroy();
    super.destroy({ children: true });
  }
}
